import { Component } from '@theme/component';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * Header search control: icon expands into an input on desktop and drives the
 * existing search modal / predictive search field.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} toggle - Icon button that opens search.
 * @property {HTMLFormElement} form - Expanded search form.
 * @property {HTMLInputElement} input - Header search input.
 *
 * @extends {Component<Refs>}
 */
class HeaderSearch extends Component {
  requiredRefs = ['toggle', 'form', 'input'];

  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {boolean} */
  #patchedModal = false;

  /** @type {Function | null} */
  #originalShow = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    this.refs.toggle.addEventListener('click', this.open, { signal });
    this.refs.form.addEventListener('submit', this.#onSubmit, { signal });
    this.refs.input.addEventListener('input', this.#onInput, { signal });
    this.refs.input.addEventListener('keydown', this.#onKeyDown, { signal });
    window.addEventListener('resize', this.#onResize, { signal });

    const patch = () => {
      if (!this.isConnected) return;
      this.#patchModal();
    };

    patch();
    customElements.whenDefined('dialog-component').then(patch);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  get #isDesktop() {
    return window.matchMedia('(min-width: 992px)').matches;
  }

  /** @returns {HTMLElement & { showDialog?: Function, closeDialog?: Function, refs?: { dialog?: HTMLDialogElement } } | null} */
  get #modal() {
    return document.getElementById('search-modal');
  }

  /** @returns {HTMLDialogElement | null} */
  get #dialog() {
    return this.#modal?.refs?.dialog ?? this.#modal?.querySelector('dialog') ?? null;
  }

  /** @returns {HTMLInputElement | null} */
  get #modalInput() {
    return this.#modal?.querySelector('[ref="searchInput"]') ?? null;
  }

  /**
   * Open search: expand the header field on desktop, otherwise open the modal.
   * @param {Event} [event]
   */
  open = (event) => {
    event?.preventDefault();

    if (!this.#isDesktop) {
      this.#openCompact();
      return;
    }

    this.#openDesktop();
  };

  close = () => {
    this.#collapse();
    const modal = this.#modal;
    const dialog = this.#dialog;
    if (dialog?.open) {
      modal?.closeDialog?.() ?? dialog.close();
    }
  };

  #collapse() {
    this.classList.remove('is-open');
    document.documentElement.classList.remove('search-open');
    this.refs.toggle.setAttribute('aria-expanded', 'false');
    this.refs.form.hidden = true;
    this.refs.input.value = '';
    this.#modal?.classList.remove('search-modal--from-header');
    if (this.#dialog) {
      this.#dialog.style.top = '';
      unlockScroll(this.#dialog);
    }
  }

  #expand() {
    this.classList.add('is-open');
    document.documentElement.classList.add('search-open');
    this.refs.toggle.setAttribute('aria-expanded', 'true');
    this.refs.form.hidden = false;
  }

  #openCompact() {
    this.#collapse();
    const modal = this.#modal;
    if (!modal) return;

    const show = this.#originalShow ?? modal.showDialog?.bind(modal);
    show?.();
  }

  #openDesktop() {
    const modal = this.#modal;
    const dialog = this.#dialog;
    if (!modal || !dialog) return;

    this.#expand();
    modal.classList.add('search-modal--from-header');
    lockScroll(dialog);

    const reveal = () => {
      this.#syncOverlayTop();
      if (!dialog.open) {
        dialog.show();
        modal.dispatchEvent(new DialogOpenEvent());
      }
      this.#syncOverlayTop();
      this.#focusInput();
      this.#syncToModal();
    };

    requestAnimationFrame(reveal);
  }

  #focusInput() {
    requestAnimationFrame(() => {
      this.refs.input.focus();
      const modalInput = this.#modalInput;
      if (modalInput) modalInput.tabIndex = -1;
    });
  }

  #syncToModal() {
    const modalInput = this.#modalInput;
    if (!modalInput) return;

    const next = this.refs.input.value;
    if (modalInput.value === next) return;

    modalInput.value = next;
    modalInput.dispatchEvent(
      new InputEvent('input', {
        bubbles: true,
        composed: true,
        inputType: next.length ? 'insertText' : 'deleteContentBackward',
        data: next.length ? next : null,
      })
    );
  }

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const query = this.refs.input.value.trim();
    if (!query) event.preventDefault();
  };

  #onInput = () => {
    this.#syncToModal();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    const predictive = this.#modal?.querySelector('predictive-search-component');
    if (predictive && typeof predictive.onSearchKeyDown === 'function') {
      predictive.onSearchKeyDown(event);
    }
  };

  #syncOverlayTop() {
    const topRow = document.querySelector('.header__row--top');
    if (!(topRow instanceof HTMLElement)) return;

    // Viewport Y of the bottom edge of the top header row (announcement bar sits above it).
    const top = Math.max(0, Math.round(topRow.getBoundingClientRect().bottom));
    const value = `${top}px`;

    document.documentElement.style.setProperty('--search-overlay-top', value);
    document.body.style.setProperty('--search-overlay-top', value);

    const dialog = this.#dialog;
    if (dialog) dialog.style.top = value;
  }

  #onResize = () => {
    if (!this.classList.contains('is-open')) return;
    if (!this.#isDesktop) {
      this.close();
      return;
    }
    this.#syncOverlayTop();
  };

  #patchModal() {
    const modal = this.#modal;
    if (!modal || this.#patchedModal) return;
    if (typeof modal.showDialog !== 'function') return;
    this.#patchedModal = true;

    const { signal } = this.#controller;

    modal.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        this.#collapse();
        const modalInput = this.#modalInput;
        if (modalInput) modalInput.tabIndex = 0;
      },
      { signal }
    );

    modal.addEventListener(
      DialogOpenEvent.eventName,
      () => {
        if (!this.#isDesktop) return;
        this.#expand();
        this.#focusInput();
      },
      { signal }
    );

    const originalShow = typeof modal.showDialog === 'function' ? modal.showDialog.bind(modal) : null;
    this.#originalShow = originalShow;
    if (originalShow) {
      modal.showDialog = () => {
        if (this.#isDesktop) {
          this.#openDesktop();
          return;
        }
        this.#openCompact();
      };
    }

    this.#dialog?.addEventListener(
      'click',
      (event) => {
        if (!modal.classList.contains('search-modal--from-header')) return;
        if (event.target === this.#dialog) this.close();
      },
      { signal }
    );

    this.#dialog?.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        if (!modal.classList.contains('search-modal--from-header')) return;
        event.preventDefault();
        this.close();
      },
      { signal }
    );
  }
}

if (!customElements.get('header-search')) {
  customElements.define('header-search', HeaderSearch);
}
